<template>
    <div class="page-content">
        <b-row class="py-3">
            <b-col class="title-wrapper">
                <div class="d-flex flex-row">
                    <h1>
                        {{ soapName }}
                        <input
                            type="hidden"
                            id="soap-id"
                            name="soapId"
                            :value="soapId"
                        />
                        <!-- For debugging in CI -->
                        <RedirectToApp
                            :ciUrl="`/apps/soap/soap/view/${$route.params.id}?forceci=true`"
                        />
                    </h1>
                    <h5
                        v-if="isRelatedToTreatment && useRelatedToTreatment"
                        class="pt-2 pl-2"
                    >
                        <span class="badge badge-secondary">{{
                            soapDetails.treatment_status_label
                        }}</span>
                    </h5>
                </div>
                <div v-if="isSpeechEnabled">
                    <button class="speech" @click="isOpenModalSpeech = true">
                        <i class="far fa-comment-dots"></i>
                        <span>Speech</span>
                    </button>
                </div>
            </b-col>
            <b-col class="text-right">
                <b-button-group class="mx-1 button-arrow">
                    <b-button
                        v-if="
                            _.isEmpty(soapDetails) ||
                            (soapDetails &&
                                _.isEmpty(soapDetails.previous_encoded_soap_id))
                        "
                        class="no-pointer"
                        disabled
                    >
                        <span
                            class="fc-icon fc-icon-chevron-left disabled-icon"
                        ></span>
                    </b-button>
                    <b-button
                        v-else
                        :to="{
                            name: 'soap',
                            params: { id: soapDetails.previous_encoded_soap_id }
                        }"
                    >
                        <span
                            class="fc-icon fc-icon-chevron-left font-weight-bolder"
                        ></span>
                    </b-button>

                    <b-button
                        v-if="
                            _.isEmpty(soapDetails) ||
                            (soapDetails &&
                                _.isEmpty(soapDetails.next_encoded_soap_id))
                        "
                        class="no-pointer"
                        disabled
                    >
                        <span
                            class="fc-icon fc-icon-chevron-right disabled-icon"
                        ></span>
                    </b-button>
                    <b-button
                        v-else
                        :to="{
                            name: 'soap',
                            params: { id: soapDetails.next_encoded_soap_id }
                        }"
                    >
                        <span
                            class="fc-icon fc-icon-chevron-right font-weight-bolder"
                        ></span>
                    </b-button>
                </b-button-group>
            </b-col>
        </b-row>

        <b-card footer-class="p-0" :key="cardKey">
            <div class="">
                <loader-profile v-if="!isDataLoaded"></loader-profile>
                <div v-if="isDataLoaded">
                    <soap-display
                        ref="soapDisplay"
                        v-if="profileMode === 'VIEW'"
                    />
                    <soap-edit
                        ref="soapEdit"
                        v-if="soapDetails && profileMode === 'EDIT'"
                        v-on="$listeners"
                        :soap="soapDetails"
                    />
                </div>
            </div>

            <template v-slot:footer no-body v-if="soapDetails">
                <b-tabs
                    card
                    lazy
                    id="nav-soap"
                    class="list tab-table-responsive"
                    v-model="activeTabIndex"
                    ref="btabs"
                >
                    <tab-medical-note
                        :key="1 + keyComponent"
                        ref="tabMedicalNote"
                        tabname="tabMedicalNote"
                        :soap="soapDetails"
                        :activeTabIndex="activeTabIndex"
                        @open-modal="openModal"
                        @change-tab="
                            reloadPage('tabRecords', HISTORY.VIEWS.RECORDS)
                        "
                        @update-tabs-count="updateTabsCount"
                        @reload="getDetails"
                        @scroll-to-element="scrollToElement"
                    />
                    <tab-records
                        v-if="_hasAccess('patient.record.view')"
                        :key="2 + keyComponent"
                        ref="tabRecords"
                        tabname="tabRecords"
                        :dialogs="$refs"
                        @open-modal="openModal"
                        @onSuccess="
                            reloadPage('tabRecords', HISTORY.VIEWS.RECORDS)
                        "
                        @scroll-to-element="scrollToElement"
                    />
                    <tab-documents
                        :key="3 + keyComponent"
                        ref="tabDocuments"
                        tabname="tabDocuments"
                        @open-modal="openModal"
                        @onSuccess="reloadPage"
                        @scroll-to-element="scrollToElement"
                    />
                    <tab-amendments
                        v-if="isLocked && _hasAccess('soap.amendment.view')"
                        :key="4 + keyComponent"
                        ref="tabAmendments"
                        tabname="tabAmendments"
                        @open-modal="openModal"
                        @onSuccess="reloadPage('tabAmendments', null, false)"
                        @scroll-to-element="scrollToElement"
                    />
                </b-tabs>
            </template>
        </b-card>

        <div
            v-if="(soapDetails && soapDetails.patient && soapDetails.patient.id)"
        >
            <popup-notes :patientId="soapDetails.patient.id" :key="popupKey" />
            <DiagnosticNewModal
                ref="diagnosticNewModal"
                :isVital="isVital"
                @reload="reloadVitalDiagnosticNewModal"
                :patientId="soapDetails.patient.id"
                :soapId="soapDetails.id"
            />
            <modal-new-attachment
                @reload="
                    reloadPage('tabDocuments', DOCUMENTS.VIEWS.ATTACHMENTS)
                "
                :patientId="soapDetails.patient.id"
            />
            <modal-new-letter :patientDetails="soapDetails.patient" />
            <modal-new-webform
                ref="modalNewWebform"
                :soapId="soapDetails.id"
                :patient="soapDetails.patient"
            />
            <modal-diagnosis
                ref="modalDiagnosis"
                :soapId="soapDetails.id"
                :patientId="soapDetails.patient.id"
                @onsuccess="getDiagnosisList"
            />
            <modal-new-section
                ref="modalAddSection"
                @reload="reloadPage('tabMedicalNote', null, false)"
            />
            <modal-snippet ref="modalSnippet" @reload="reloadPage()" />
            <modal-new-amendment
                ref="modalNewAmendment"
                @onSuccess="reloadPage('tabAmendments', null, false)"
            />
        </div>
        <modal-record ref="modalRecord" @onSuccess="onSuccessModalRecord" />
        <modal-bundle
            ref="modalBundle"
            @reload="reloadPage('tabRecords', HISTORY.VIEWS.RECORDS)"
        />

        <soap-speech
            v-if="isOpenModalSpeech"
            @close="isOpenModalSpeech = false"
        />
        <edit-activity ref="editModal" />
        <create-update-record ref="createUpdateRecordModal" />
        <new-vital ref="newVitalModal" />
        <start-task ref="startTaskModal" />
        <delete-activity ref="deleteActivityModal" />
        <activity-history ref="modalActivityHistory" />
        <NewBundle ref="NewBundle"></NewBundle>
        <activity-summary ref="summaryModal" />
    </div>
</template>

<script>
/**
 * @name Soap Details Page
 * @description soap display, details and navbar
 * @see apps/application/views/staff/patient/soap/navbar.php
 * @see apps/application/views/staff/patient/soap/view.php
 */

import unsavePrompt from "@/mixins/unsave-prompt.js";
import SoapDisplay from "@/components/soap/soap-display";
import SoapEdit from "@/components/soap/soap-edit";
import LoaderProfile from "@/components/elements/loader-profile";
import TabMedicalNote from "@/module/patient/tabs/soap/tab-medical-note";
import TabRecords from "@/module/patient/tabs/soap/tab-records";
import TabDocuments from "@/module/patient/tabs/soap/tab-documents";
import TabAmendments from "@/module/patient/tabs/soap/tab-amendments";
import ModalRecord from "@/module/commons/record/create-update";
import ModalBundle from "@/components/bundle/modal";
import DiagnosticNewModal from "@/components/patient-diagnostic/diagnostic-new-modal";
import ModalNewAttachment from "@/components/patient-documents/document-add-attachment-modal";
import ModalNewLetter from "@/components/patient-documents/document-add-letter-modal";
import ModalNewWebform from "@/components/webform/modal-new-webform";
import ModalDiagnosis from "@/components/commons/modal-diagnosis";
import ModalNewSection from "@/components/soap/new-section-modal.vue";
import ModalNewAmendment from "@/components/soap-amendments/modal-new-amendment";
import PopupNotes from "@/components/patient/popup-notes";
import HISTORY from "@/constants/history";
import DOCUMENTS from "@/constants/documents";
import AMENDMENTS from "@/constants/amendments";
import ModalSnippet from "@/components/soap/new-snippet";
import CTABMEDICALNOTE from "@/constants/tab-medical-note";
import ScheduleHelper from "@/module/schedule/helper";
import { findIndex, isEmpty, clone, some, isUndefined, find } from "lodash";
import RedirectToApp from "@/components/commons/redirect-to-app.vue";
import SoapSpeech from "./soap-speech.vue";
import EditActivity from "@/module/schedule/treatment-board/activity";
import CreateUpdateRecord from "@/module/commons/record/create-update.vue";
import NewVital from "@/module/vital/new-multiple-vital";
import StartTask from "@/module/schedule/treatment-board/start-task";
import DeleteActivity from "@/module/schedule/treatment-board/delete-activity";
import ActivityHistory from "@/module/schedule/treatment-board/activity-history";
import NewBundle from "@/module/schedule/treatment-board/treatment-bundle";
import ActivitySummary from "@/module/schedule/treatment-board/summary";

const DEBOUNCE_SOAP_UPDATE_IN_MS = 500;

export default {
    mixins: [unsavePrompt, ScheduleHelper],
    components: {
        SoapDisplay,
        SoapEdit,
        LoaderProfile,
        TabMedicalNote,
        TabRecords,
        TabDocuments,
        TabAmendments,
        PopupNotes,
        DiagnosticNewModal,
        ModalRecord,
        ModalBundle,
        ModalNewAttachment,
        ModalNewLetter,
        ModalNewWebform,
        ModalDiagnosis,
        ModalNewSection,
        ModalNewAmendment,
        ModalSnippet,
        RedirectToApp,
        SoapSpeech,
        EditActivity,
        CreateUpdateRecord,
        NewVital,
        StartTask,
        DeleteActivity,
        ActivityHistory,
        NewBundle,
        ActivitySummary
    },
    props: {
        profileMode: {
            type: String
        }
    },
    data() {
        return {
            HISTORY,
            DOCUMENTS,
            CTABMEDICALNOTE,
            isDataReloading: true,
            isLoading: false,
            record: {},
            keyComponent: 0,
            cardKey: 0,
            activeTabIndex: 0,
            firstTimeLoaded: false,
            isEditTypeFee: false,
            categories: {
                tabRecords: HISTORY.VIEWS.RECORDS
            },
            isVital: false,
            isOpenRecordTab: true,
            isOpenModalSpeech: false,
            isSpeechEnabled: false,
            preventLoadingAnimation: false,
            lastPingResult: null,
            lastPingCheckedAt: null
        };
    },
    sockets: {
        "v-broadcast"(params) {
            // check if this message comes from same treatment id before allowing to update.
            let relatedTreatmentId =
                params?.params?.treatment_id || params?.treatmentId;
            if (relatedTreatmentId != this.soapDetails?.treatment_board_id) {
                return;
            }

            if (params.action === "refresh-treatment") {
                this.preventLoadingAnimation = true;
                this.reloadPage(
                    "tabRecords",
                    HISTORY.VIEWS.ACTIVITIES,
                    true,
                    false
                );
            }

            if (params.action === "reroute-treatment-details") {
                this.$router.push({ name: "treatment" });
                this._message(
                    "The treatment was successfully removed by a user."
                );
            }

            if (params.action === "activity-check") {
                if (some(this.$store.state.locked_activity?.locked)) {
                    this.$socket.client.emit("v-broadcast", {
                        params: this.$store.state.locked_activity.locked,
                        action: "activity-lock",
                        businessId: this._setting.business_id
                    });
                }
            }

            if (params.action === "activity-lock") {
                let activities = find(this.treatments, {
                    id: params.params.treatment_id
                })?.activities;
                if (!isUndefined(activities)) {
                    this.$set(
                        find(activities, { id: params.params.id }),
                        "locked",
                        params.params
                    );
                }
                params.params.message =
                    "This activity currently being edited by " +
                    params.params.staff.fullname;
                this.$store.commit("locked_activity_add", params.params);
            }

            if (params.action === "activity-unlock") {
                let activities = find(this.treatments, {
                    id: params.params.treatment_id
                })?.activities;
                if (!isUndefined(activities)) {
                    this.$set(
                        find(activities, { id: params.params.id }),
                        "locked",
                        null
                    );
                }

                this.$store.commit("locked_activity_remove", params.params);
            }
        }
    },
    computed: {
        soapId() {
            return this.soapDetails ? this.soapDetails.id : null;
        },
        soapDetails() {
            return this.$store.getters["soap/soapDetails"];
        },
        soapName() {
            if (!this.isDataLoaded) {
                return "Loading...";
            }
            return this.soapDetails ? this.soapDetails.label : "";
        },
        isDataLoaded() {
            if (this.preventLoadingAnimation) {
                return true;
            }
            return !isEmpty(this.soapDetails) && !this.isDataReloading;
        },
        routeParam() {
            return this.$route.params.id;
        },
        popupKey() {
            return this.$store.getters["patient/popupKey"];
        },
        form_dirty() {
            //TODO : check if need this for soap page
            return this.$store.getters["soap/isDirty"];
        },
        isLocked() {
            return this.$store.getters["soap/isLocked"];
        },
        isRelatedToTreatment() {
            return this.soapDetails?.treatment_board_id;
        },
        useRelatedToTreatment() {
            return (
                this.soapDetails?.treatment_status_id ==
                    this.TREATMENT_STATUS.TREATMENT ||
                this.soapDetails?.treatment_status_id ==
                    this.TREATMENT_STATUS.POSTTREATMENT
            );
        }
    },
    watch: {
        async routeParam() {
            // Before redirect to other medical note page by using next and previous button
            await this.waitForSoapChanges();

            this.$store.commit("soap/setSoapDetails", null);
            this.reloadSoapPage();
            this.cardKey++;
        },
        useRelatedToTreatment(status) {
            if (status) {
                this.$root.$on("treatment-board-refresh", params => {
                    if (params.treatment_board_id) {
                        this.reloadPage("tabRecords", HISTORY.VIEWS.ACTIVITIES);
                    }
                    this.$socket.client.emit("v-broadcast", {
                        action: "refresh-treatment",
                        businessId: this._setting.business_id
                    });
                });
            } else {
                this.$root.$off("treatment-board-refresh");
            }
        }
    },
    async mounted() {
        this.$store.commit("soap/setSoapPageInstance", this.uniqid());

        document.addEventListener(
            "visibilitychange",
            this.handleVisibilityChange
        );
        await this._getSetting();
        this.$store.dispatch("soap/checkSoapEnableBeta");

        this._getStatus().then(statuses => {
            this.$store.commit(
                "status",
                statuses.map(status => ({
                    ...status,
                    icon: this.statusClass(status.id)
                }))
            );
        });

        this.reloadPageDetails().then(() => {
            // After soap details been rendered
            if (this.$refs.tabMedicalNote) {
                this.$refs.tabMedicalNote.showList();
            }
        });

        this.$root.$on("refresh-soap-page", (params = {}) => {
            this.reloadPage(
                params.tabName,
                params.historyList,
                params.isReloadPageDetails
            );
        });
        this.$root.$on("open-medical-record-modal", (params = {}) => {
            this.openModal(HISTORY.VIEWS.RECORDS, params);
        });

        this.$root.$on("antech-status-update", () =>
            this.reloadPage("tabRecords", HISTORY.VIEWS.RECORDS)
        );

        this.checkSpeechFeature();
    },
    destroyed() {
        this.$root.$off("refresh-soap-page");
        this.$root.$off("open-medical-record-modal");
        this.$store.dispatch("soap/clearSoapDetails");
        document.removeEventListener(
            "visibilitychange",
            this.handleVisibilityChange
        );
        this.$store.commit("soap/setSoapPageInstance", null);
        this.$root.$off("antech-status-update", () =>
            this.reloadPage("tabRecords", HISTORY.VIEWS.RECORDS)
        );
        this.$root.$off("treatment-board-refresh");
    },
    methods: {
        async checkSpeechFeature() {
            setTimeout(() => {
                // $root.setting.feature_flag tidak peduli `is_enabled` dalam DB,
                // jadi semua yang ada akan tersenarai walaupun is_enabled 0 -- TODO / FIXME
                let flags = this.$root.setting?.feature_flag ?? "";
                let isEnabled = flags.split(",").includes("ai_speech_to_text");
                this.isSpeechEnabled = isEnabled;
            }, 600);
        },
        async beforeCheckDirtyFormHook() {
            // Before redirect to other view page
            await this.waitForSoapChanges();
        },
        async waitForSoapChanges() {
            // VET-5499 - To avoid data loss when click next/previous/other soap while the soap content is been edited and still not saved yet
            // While waiting, show message the soap content is saving
            let self = this;
            await this._sleep(DEBOUNCE_SOAP_UPDATE_IN_MS);
            const messageId = `message-${this.uniqid()}`;
            await this.$store.dispatch("soap/waitForSoapChanges", {
                showMessage: () => {
                    self.$root.$emit(
                        "message",
                        "Saving changes...",
                        "info",
                        null,
                        null,
                        { id: messageId }
                    );
                },
                closeMessage: () => {
                    self.$root.$emit("close-message", messageId);
                }
            });
        },
        async checkSocketConnectivity() {
            const now = Date.now();
            const lastChecked = this.lastPingCheckedAt || 0;
            const shouldCheckPing = now - lastChecked > 2 * 60 * 1000; // 2 minutes interval
            let isConnected = this.lastPingResult;
            if (shouldCheckPing) {
                this.lastPingCheckedAt = now;
                isConnected = await this.$refs.tabMedicalNote?.checkSocketIsConnected?.();
                this.lastPingResult = isConnected;
            }
            return isConnected;
        },
        async handleVisibilityChange() {
            if (
                document.visibilityState === "visible" &&
                this.activeTabIndex === 0 // tab medical notes
            ) {
                // VET-5499 - Apply 500 ms delay the same as debounce from the input field so that the component editable can triggered the update successfully
                // No longer needed as no more has reload the medical note upon on changing tab
                // await this._sleep(DEBOUNCE_SOAP_UPDATE_IN_MS);

                // VET-5423 - When open annotate image, changing tabs to other and back to soap will reset the tab to soap content tab and destroy the child components
                // hence skip change tab and increment keyComponent when the annotate image opened
                let isSetActiveTabIndex = true;

                const elAnnotationImage = document.getElementById(
                    "annotate-editor"
                );

                if (elAnnotationImage) {
                    const isDisplayed =
                        window.getComputedStyle(elAnnotationImage).display !==
                        "none";
                    const isVisibilityHidden =
                        window.getComputedStyle(elAnnotationImage)
                            .visibility === "hidden";
                    const hasDimensions =
                        elAnnotationImage.offsetWidth > 0 &&
                        elAnnotationImage.offsetHeight > 0;

                    const isElAnnotationVisible =
                        isDisplayed && !isVisibilityHidden && hasDimensions;
                    isSetActiveTabIndex = !isElAnnotationVisible; // Dont set the activeTabIndex to avoid the annotation page closed
                }

                // VET-9124 - Check whether the socket been disconnected, if yes, then reload the medical note content
                const isConnected = await this.checkSocketConnectivity();
                if (!isConnected && !this.isLocked && navigator?.onLine) {
                    // VET-9124 - navigator?.onLine - Only online will reload, if offline, will do nothing as when network return online, the page will reload
                    console.error(
                        "(Visibilitychange Event) Socket is disconnected. Reloading..."
                    );
                    this.reloadPage(
                        "tabMedicalNote",
                        null,
                        false,
                        isSetActiveTabIndex
                    );
                    this.updateTabsCount(["tabRecords", "tabDocuments"]);
                    this.lastPingResult = true;
                }
            } else {
                var element = $(document.activeElement);
                var lastFocused =
                    element.attr("id") !== undefined
                        ? element.attr("id")
                        : null;
                if (lastFocused !== undefined && this.soapId) {
                    // x and y to local storage
                    var x = window.scrollX;
                    var y = window.scrollY;
                    sessionStorage.setItem(
                        `${this.soapId}_lastScrollPos`,
                        JSON.stringify({
                            x: x,
                            y: y,
                            lastFocusedElementId: lastFocused
                        })
                    );
                }
            }
        },
        reloadVitalDiagnosticNewModal(view) {
            this.reloadPage("tabRecords", view);
        },
        async reloadPageDetails() {
            await this.getDetails();
            await this.reloadHistoryCountSoap();
            await this.reloadAmendmentsCountSoap();
            await this.reloadDocumentsCountSoap();
        },
        /////////////////
        // FLOW SAVING //
        /////////////////
        /**
         * @method savingCheckFormValid
         * @description called from soap/index.vue and then call function checkFormIsValid in soap/create-update.vue
         * @return {Promise}
         */
        savingCheckFormValid() {
            return this.$refs.soapEdit.checkFormIsValid();
        },
        /**
         * @method savingEditSoap
         * @description called from soap/index.vue and then call function proceedSave in soap/create-update.vue with
         * boolean isUpdateUses
         * @return {void}
         */
        savingEditSoap() {
            this.$refs.soapEdit.save();
        },
        /**
         * @method saved
         * @description called from soap/create-update.vue means the soap has been saved
         * @return {void}
         */
        async reloadSoapPage() {
            this.resetTab();
            await this.reloadPageDetails();
            this.$refs.tabMedicalNote?.showList();
        },
        /**
         * @method resetTab
         * @description To reset the tab to the first tab each time open edit/cancel edit and after edit
         */
        resetTab() {
            this.keyComponent++;
            this.activeTabIndex = 0;
        },
        async getDetails() {
            let self = this;
            self.isDataReloading = true;
            const encodedId = this.$route.params.id;
            if (encodedId) {
                await this.$store
                    .dispatch("soap/getSoapDetails", encodedId)
                    .then(res => {
                        if (
                            res &&
                            res.message &&
                            res.message.code == 200 &&
                            res.resource.isactive
                        ) {
                            self.dynamicPageTitle(
                                "Medical Note - " +
                                    res.resource.patient.fullname
                            );
                            self.storeAudit();
                            this.$store.dispatch(
                                "patient/getReminders",
                                res.resource.patient.id
                            );
                            if (res.resource && res.resource.patient) {
                                this.reloadPopupNotes();
                            }
                            self.isDataReloading = false;
                            self.preventLoadingAnimation = false;
                        } else {
                            this.$router.push({
                                name: "patient_profile",
                                params: {
                                    id: this.soapDetails.patient.encoded_id
                                }
                            });
                        }
                    });
            } else {
                this.$router.push({ name: "patient_dashboard" });
            }
        },
        async updateTabsCount(tabs = []) {
            if (Array.isArray(tabs) && tabs.length > 0) {
                if (tabs.includes("tabRecords")) {
                    await this.reloadHistoryCountSoap();
                }
                if (tabs.includes("tabDocuments")) {
                    this.reloadDocumentsCountSoap();
                }
            }
        },
        async storeAudit() {
            const fullPath = clone(this.$router.currentRoute.fullPath);
            await this._getSetting();
            await this._saveAudit({
                action: "view",
                path: "/soap/soap/view." + this.soapId,
                reference_id: this.soapId,
                fullPath
            });
        },
        async reloadHistoryCountSoap() {
            if (this.soapId) {
                await this.$store.dispatch(
                    "soap/getHistoryCountSoap",
                    this.soapId
                );
            }
        },
        async reloadAmendmentsCountSoap() {
            if (this.soapId) {
                await this.$store.dispatch(
                    "soap/getAmendmentsCountSoap",
                    this.soapId
                );
            }
        },
        async reloadDocumentsCountSoap() {
            if (this.soapId) {
                await this.$store.dispatch(
                    "soap/getDocumentsCountSoap",
                    this.soapId
                );
            }
        },
        reloadPopupNotes() {
            this.$store.commit("patient/clearPopupNotes"); // clear popup notes by default

            this.$store.dispatch(
                "patient/loadPopupNotes",
                this.soapDetails.patient.id
            );
        },
        reloadTabList(tabName, tabList) {
            if (tabList && this.$refs[tabName]) {
                this.$refs[tabName].showList(tabList);
            }
        },
        async reloadPage(
            tabName = "tabMedicalNote",
            tabList = null,
            isReloadPageDetails = true,
            isSetActiveTabIndex = true
        ) {
            // VET-5499 - To avoid data loss when user jump to other tab and back to current tab immediately causing the save field API not triggered
            await this.$store.dispatch("soap/waitForSoapChanges");
            if (isSetActiveTabIndex) {
                this.keyComponent++;
            }
            await this.$nextTick();
            if (isReloadPageDetails) {
                await this.getDetails();
            }

            let activeTabIndex = 0;
            switch (tabName) {
                case "tabRecords":
                    // reload count for document as well since some record also trigger create letter, etc.
                    this.updateTabsCount(["tabRecords", "tabDocuments"]);
                    activeTabIndex = 1;
                    break;
                case "tabDocuments":
                    await this.reloadDocumentsCountSoap();
                    activeTabIndex = 2;
                    break;
                case "tabAmendments":
                    await this.reloadAmendmentsCountSoap();
                    activeTabIndex = 3;
                    break;
            }

            if (isSetActiveTabIndex) {
                this.activeTabIndex = activeTabIndex;
            }

            if (tabName === "tabMedicalNote") {
                this.$refs.tabMedicalNote?.showList();
            } else {
                this.reloadTabList(tabName, tabList);
            }
        },
        findTabIndex(tabname) {
            return findIndex(this.$refs.btabs.$children, function (tab) {
                return tab.$attrs.tabname == tabname;
            });
        },
        onSuccessModalRecord() {
            if (this.isOpenRecordTab) {
                this.reloadPage("tabRecords", HISTORY.VIEWS.RECORDS);
            } else {
                if (this.$refs.tabMedicalNote) {
                    this.$refs.tabMedicalNote.getRecordList(true);
                }
            }
        },
        openModal(type, params = null) {
            let patient = this.soapDetails.patient;
            let ownerArr = patient.primary_owner;
            let clientId = ownerArr.length > 0 ? ownerArr[0].id : "";

            switch (type) {
                case HISTORY.VIEWS.RECORDS:
                    this.isOpenRecordTab = params?.isOpenRecordTab ?? true;

                    if (params && params.recordParams) {
                        // Edit from soap-content-record-list
                        this.$refs.modalRecord.open(params.recordParams);
                    } else {
                        // New
                        this.$refs.modalRecord.open({
                            action: "new",
                            clientID: clientId,
                            patient: {
                                id: patient.id,
                                primaryOwner: patient.primary_owner,
                                weight: patient.weight
                            },
                            staffId:
                                this.soapDetails.prescribed_by?.id ||
                                this.soapDetails.default_provider?.id,
                            soapDate: this.soapDetails.date,
                            soapId: this.soapDetails.id
                        });
                    }
                    break;
                case HISTORY.VIEWS.VITALS:
                    this.isVital = true;
                    this.$refs.diagnosticNewModal.open();
                    break;
                case HISTORY.VIEWS.DIAGNOSTIC:
                    this.isVital = false;
                    this.$refs.diagnosticNewModal.open();
                    break;
                case HISTORY.VIEWS.RECORDS_BUNDLE:
                    this.$refs.modalBundle.open("PATIENT", patient);
                    break;
                case DOCUMENTS.VIEWS.WEBFORMS:
                    this.$refs.modalNewWebform.open();
                    break;
                case HISTORY.VIEWS.DIAGNOSES:
                    this.$refs.modalDiagnosis.open();
                    break;
                case AMENDMENTS.VIEWS.UPDATE:
                    this.$refs.modalNewAmendment.open(params);
                    break;
                case CTABMEDICALNOTE.VIEWS.SNIPPET:
                    this.$refs.modalSnippet.open();
                    break;
                case CTABMEDICALNOTE.VIEWS.SECTION:
                    this.$refs.modalAddSection.open();
                    break;
                case HISTORY.VIEWS.ACTIVITIES:
                    if (params) {
                        this.addBundle(patient);
                    } else {
                        this.addActivity(patient);
                    }
                    break;
            }
        },
        getDiagnosisList() {
            this.$root.$emit("diagnosis-list:get-list");
        },
        scrollToElement(elementId) {
            let isPageOnElement = false;
            // Function to check if the current scroll is not at the element with the given ID
            const element = document.getElementById(elementId);

            if (!element) {
                return false;
            }

            const elementPosition =
                element.getBoundingClientRect().top + window.scrollY;
            const currentScrollPosition =
                window.scrollY || document.documentElement.scrollTop;

            // Check if the current scroll position is different from the element's position
            isPageOnElement = currentScrollPosition !== elementPosition;

            if (isPageOnElement) {
                this.$nextTick(() => {
                    // When the tab is re-rendered, the page will scroll top
                    document.getElementById(elementId).scrollIntoView({
                        behavior: "smooth"
                    });
                });
            }
        },
        addActivity(patient) {
            let params = { patient: patient };
            this.$refs.editModal.open({
                ...params,
                action: "new",
                getDataFromFetchPatient: Boolean(params)
            });
        },
        addBundle(patient) {
            let params = { patient: patient };
            this.$refs.NewBundle.open({
                ...params,
                action: "new",
                disabledSoapChange: true,
                getDataFromFetchPatient: Boolean(params)
            });
        }
    }
};
</script>
<style lang="scss" scoped>
#nav-soap .dropdown-menu {
    z-index: 11;
}
.title-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;

    h1 {
        margin: 0;
    }

    button.speech {
        border: none;
        padding: 3px 10px;
        display: flex;
        align-items: center;
        gap: 5px;
        border-radius: 6px;
        border: 1px solid #a0a1ad;
        background: linear-gradient(180deg, #e7e7e7, #f7f7f7);
        box-shadow: inset 0px 1px 0 #ffffff;
        font-weight: 500;
        outline: none;

        &:hover {
            border: 1px solid #56782b;
            background: #e1ead4;
        }
    }
}
</style>
